import { create } from "zustand";
import { persist } from "zustand/middleware";
import { atelierApi, type MyoAction } from "@services/api/atelier";
import { disponibilitesApi } from "@services/api/disponibilites";
import {
  WorkshopJobStatus,
  WorkshopSpecialty,
  WorkshopStatusConfig,
  WorkshopWorker,
  WorkshopJob,
  WorkCharge,
  CarWindow,
} from "../types/garage/atelier";
import { useDataStore } from "./user_org";
import { usePlanningStore, type CompactionMode } from "./planningStore";
import type { User } from "../types/user";
import type { OrdreStatus } from "../types/ordre";
import { collection, query, where, onSnapshot, Timestamp } from "firebase/firestore";
import { showNotification } from "context/notification.service";
import firebaseService from "@services/firebase/index";
import { setBackgroundTask } from "@services/backgroundTasks";

// Relecture complète après un passage serveur (voir startRealtimeListener) : groupée sur
// 5 s, au plus une toutes les 30 s, jamais pendant une écriture MyO en attente.
let resyncTimer: ReturnType<typeof setTimeout> | null = null;
let lastResyncAt = 0;
function scheduleServerResync(orgId: string) {
  if (resyncTimer) return;
  const wait = Math.max(5_000, lastResyncAt + 30_000 - Date.now());
  resyncTimer = setTimeout(() => {
    resyncTimer = null;
    const s = useAtelierStore.getState();
    if (s.myoPendingJobIds.length > 0) {
      scheduleServerResync(orgId);
      return;
    }
    lastResyncAt = Date.now();
    s.fetchData(orgId, true, true);
  }, wait);
}

/** Map a Firestore workshop_jobs doc to a frontend WorkshopJob */
function firestoreDocToJob(id: string, d: Record<string, any>): WorkshopJob {
  return {
    id,
    clientName: d.client_name || "",
    plate: d.plate || "",
    hours: d.hours || 0,
    workerId: d.worker_id || "",
    date: d.date || "",
    startHour: d.start_hour || 0,
    durationHours: d.duration_hours || 0,
    status: d.status || "en_attente",
    specialty: d.specialty || "autre",
    hasCarIcon: d.has_car_icon || false,
    ordre_id: d.ordre_id || undefined,
    org_id: d.org_id || "",
    autoGenerated: d.auto_generated || false,
    source: d.source || undefined,
    bookingId: d.booking_id || undefined,
    vehicleName: d.vehicle_name || undefined,
    interventions: d.interventions || undefined,
    myoLocalOnly: d.myo_local_only?.reason || undefined,
  };
}

/**
 * Garage « Carvis fait foi » (servicebox.carvis_master) : planning atelier 100 % Carvis.
 * Rien n'est plus relu de MyO ni écrit dans MyO, donc les créneaux et charges venus de
 * MyO se modifient comme les autres (/atelier/jobs, /atelier/charges).
 */
export const isCarvisMaster = () => !!useDataStore.getState().orgData?.servicebox?.carvis_master;

/**
 * Un créneau recopié de MyO appartient au réconciliateur ServiceBox : modifié seulement
 * ici, il reviendrait à sa place à la synchro suivante. Ses actions (déplacer, scinder,
 * retirer) passent donc par le back, qui les écrit dans MyO, les relit, puis réaligne
 * Carvis (POST /atelier/myo/*) — voir runMyo. Sauf si Carvis fait foi (isCarvisMaster).
 */
export const isMirroredJob = (j: Pick<WorkshopJob, "source">) => j.source === "servicebox" && !isCarvisMaster();
/** Charge recopiée de MyO (« à planifier ») : la placer l'écrit dans MyO (sauf si Carvis fait foi). */
export const isMirroredCharge = (c: Pick<WorkCharge, "serviceboxRdvId">) => !!c.serviceboxRdvId && !isCarvisMaster();

/** Autres morceaux posés de la même intervention (même ordre, même métier). */
export function interventionSiblings(jobs: WorkshopJob[], job: WorkshopJob): WorkshopJob[] {
  if (!job.ordre_id) return [];
  return jobs.filter(
    (j) => j.id !== job.id && j.ordre_id === job.ordre_id && j.specialty === job.specialty,
  );
}

/** Map a Firestore work_charges doc to a frontend WorkCharge */
function firestoreDocToCharge(id: string, d: Record<string, any>): WorkCharge {
  return {
    id,
    clientName: d.client_name || "",
    hours: d.hours || 0,
    status: d.status || "a_repartir",
    specialty: d.specialty || "autre",
    ordre_id: d.ordre_id || undefined,
    org_id: d.org_id || "",
    plate: d.plate || undefined,
    autoGenerated: d.auto_generated || false,
    bookingId: d.booking_id || undefined,
    serviceboxRdvId: d.servicebox_rdv_id || undefined,
  };
}

/** Roles that map to workshop specialties (used as fallback) */
const ROLE_TO_SPECIALTY: Record<string, WorkshopSpecialty> = {
  mecanicien: "mecanique",
  carrossier: "carrosserie",
  peintre: "peinture",
  diagnosticien: "diagnostique",
  // Un chef d'atelier reste un mécanicien sur le planning : en « autre », ses travaux
  // disparaissaient du filtre Mécanique (Jordan, 2026-10).
  chef_atelier: "mecanique",
};

export const JOB_STATUSES: WorkshopStatusConfig[] = [
  { id: "en_attente", label: "En attente", color: "#2563EB", bgColor: "#DBEAFE", borderColor: "#93C5FD" },
  { id: "en_cours", label: "En cours", color: "#5C6BFF", bgColor: "#CACFFF", borderColor: "#A5ADFF" },
  { id: "fin_travaux", label: "Fin de travaux", color: "#7C3AED", bgColor: "#EDE9FE", borderColor: "#C4B5FD" },
  { id: "disponible", label: "Disponible", color: "#8258FF", bgColor: "#F1E7F9", borderColor: "#D4BFFF" },
  { id: "restitue", label: "Restitué", color: "#14B8A6", bgColor: "#CCFBF1", borderColor: "#99F6E4" },
];

/** Map an ordre status to the corresponding job status for display on the atelier grid */
const ORDRE_TO_JOB_STATUS: Record<OrdreStatus, WorkshopJobStatus> = {
  rdv: "en_attente",
  a_preparer: "en_attente",
  devis_initial: "en_attente",
  commande: "en_attente",
  pret: "en_attente",
  reception: "en_attente",
  en_cours: "en_cours",
  devis_supp: "en_cours",
  accord: "en_cours",
  acompte: "en_cours",
  fin_de_travaux: "fin_travaux",
  facturation: "fin_travaux",
  solde: "fin_travaux",
  solde_paye: "fin_travaux",
  disponible: "disponible",
  restitue: "restitue",
  arrete: "en_attente",
  annule: "en_attente",
};

/** À partir de « fin de travaux », l'atelier n'a plus rien à répartir pour l'ordre :
 *  ses charges en attente ne s'affichent plus (le back les retire au changement de
 *  statut ; ceci couvre celles posées par la synchro ou avant la règle). */
export const WORKSHOP_DONE_STATUSES: ReadonlySet<string> = new Set([
  "fin_de_travaux", "facturation", "solde", "solde_paye", "disponible", "restitue", "annule",
]);

export function getJobStatusFromOrdre(ordreStatus: OrdreStatus | undefined): WorkshopJobStatus {
  if (!ordreStatus) return "en_attente";
  return ORDRE_TO_JOB_STATUS[ordreStatus] || "en_attente";
}

// Workers are fetched from API but we keep a local cache
let _cachedWorkers: WorkshopWorker[] = [];
export function getCachedWorkers(): WorkshopWorker[] {
  return _cachedWorkers;
}

let _nextId = 100;
function genId() {
  return `gen_${_nextId++}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Get Mon-Fri date range for the week containing dateStr */
function getWeekRange(dateStr: string): { date_from: string; date_to: string } {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun
  const diffToMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diffToMon);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  return { date_from: toISO(mon), date_to: toISO(fri) };
}

interface AtelierState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  /** Row-height density of the grid (compact fits more of the day on screen). */
  compaction: CompactionMode;
  /** Métiers shown in the grid. Empty array = "Tous" (all métiers). */
  activeSpecialties: string[];
  selectedWeekWorkerId: string;
  selectedWorkerIds: string[];
  jobs: WorkshopJob[];
  charges: WorkCharge[];
  workers: WorkshopWorker[];
  isLoading: boolean;
  isRefreshing: boolean;
  searchQuery: string;
  hoveredChargeId: string | null;
  highlightOrdreId: string | null;
  highlightJobId: string | null;
  carWindows: Record<string, CarWindow>;
  _fetchedOrgId: string | undefined;
  _loadedRange: { date_from: string; date_to: string } | undefined;

  /** `fresh` : le serveur ignore son cache court des listes — pour ce qu'il ne
   *  peut pas détecter seul (un RDV déplacé change la fenêtre véhicule). */
  fetchData: (orgId: string, force?: boolean, fresh?: boolean) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setCompaction: (mode: CompactionMode) => void;
  /** Toggle a métier in/out of the active filter. */
  toggleSpecialty: (s: string) => void;
  /** Reset the filter to "Tous" (show all métiers). */
  setAllSpecialties: () => void;
  setSelectedWeekWorkerId: (id: string) => void;
  toggleWorkerId: (id: string) => void;
  setSelectedWorkerIds: (ids: string[]) => void;
  setSearchQuery: (query: string) => void;
  setHoveredChargeId: (id: string | null) => void;
  setHighlightOrdreId: (id: string | null) => void;
  setHighlightJobId: (id: string | null) => void;
  /** `force` : placement hors des horaires du compagnon déjà confirmé (créneaux MyO). */
  updateJob: (id: string, updates: Partial<Pick<WorkshopJob, "startHour" | "durationHours" | "workerId" | "date">>, opts?: { force?: boolean }) => void;
  getFilteredJobs: () => WorkshopJob[];
  getVisibleWorkers: () => WorkshopWorker[];
  placeChargeAsJob: (chargeId: string, workerId: string, startHour: number, durationHours: number, date?: string, force?: boolean) => void;
  splitCharge: (chargeId: string, keepHours: number) => void;
  mergeCharges: (keepId: string, deleteId: string) => void;
  mergeJobs: (keepId: string, deleteId: string, newStartHour: number, newDurationHours: number) => void;
  /** Charge lâchée sur (ou contre) un travail du même ordre : le travail grandit jusqu'au
   *  créneau fusionné, la charge perd les heures posées (supprimée à zéro). */
  mergeChargeIntoJob: (chargeId: string, targetJobId: string, startHour: number, durationHours: number) => void;
  returnJobToCharge: (jobId: string) => void;
  /** Retire tous les morceaux posés de l'intervention et les rend en UNE charge (MyO : écrit dans MyO). */
  returnInterventionToCharge: (jobId: string) => void;
  /** Garde `keepHours` sur la grille ; le reste repart en charge « à répartir ». */
  splitJob: (jobId: string, keepHours: number) => void;
  /** Retrait d'un créneau demandé (glisser vers les charges, menu clic droit). */
  requestJobRemoval: (jobId: string) => void;
  jobRemovalPending: { jobId: string } | null;
  jobSplitPending: { jobId: string } | null;
  /** Écriture MyO en cours (libellé affiché). Les suivantes attendent en file. */
  myoBusy: string | null;
  /** Créneaux dont une écriture MyO est en file ou en cours (affichés « en cours »). */
  myoPendingJobIds: string[];
  /** Met une action MyO en file (une à la fois, dans l'ordre) ; résout à la fin de SA
   *  propre écriture. `rollback` défait l'affichage optimiste si MyO refuse. */
  runMyo: (action: MyoAction, body: Parameters<typeof atelierApi.myoAction>[1], label: string,
    opts?: { pendingIds?: string[]; rollback?: () => void; tempIds?: string[] }) => Promise<boolean>;
  /** MyO a refusé un créneau hors des horaires du compagnon : on propose de forcer. */
  myoForcePending: { action: MyoAction; body: Parameters<typeof atelierApi.myoAction>[1]; label: string; message: string } | null;
  setMyoForcePending: (p: AtelierState["myoForcePending"]) => void;
  setJobRemovalPending: (p: { jobId: string } | null) => void;
  setJobSplitPending: (p: { jobId: string } | null) => void;
  moveChargeToStatus: (chargeId: string, newStatus: WorkCharge["status"]) => void;
  /** Retire une charge pour de bon (charge orpheline, ordre disparu…). */
  deleteCharge: (chargeId: string) => void;
  confirmJob: (jobId: string) => void;
  invalidateCarWindows: (ordreId: string) => void;
  _realtimeUnsubs: (() => void)[];
  startRealtimeListener: (orgId: string) => void;
  stopRealtimeListener: () => void;
}

// ── File des écritures MyO ─────────────────────────────────────────────────────
// Une écriture à la fois (le compte ServiceBox a UN onglet actif), mais on n'en refuse
// plus aucune : « Une écriture MyO est déjà en cours » faisait perdre le geste. Chacune
// a sa ligne dans les tâches de fond (comme l'envoi Alpha) et son créneau « en cours ».
interface MyoTask {
  id: string;
  action: MyoAction;
  body: Parameters<typeof atelierApi.myoAction>[1];
  label: string;
  pendingIds: string[];
  tempIds: string[];
  rollback?: () => void;
  resolve: (ok: boolean) => void;
}
const myoQueue: MyoTask[] = [];
let myoRunning = false;
let myoSeq = 0;

type StoreSet = (partial: Partial<AtelierState> | ((s: AtelierState) => Partial<AtelierState>)) => void;

async function pumpMyo(set: StoreSet, get: () => AtelierState) {
  if (myoRunning) return;
  myoRunning = true;
  try {
    while (myoQueue.length > 0) {
      const task = myoQueue.shift()!;
      set({ myoBusy: task.label });
      setBackgroundTask(task.id, { status: "running", detail: "Écriture dans MyO…" });
      let ok = false;
      let refetch = false;
      try {
        const result = await atelierApi.myoAction(task.action, task.body);
        ok = true;
        // Rafraîchissement ciblé : les lignes du RDV relues par le back remplacent les
        // nôtres (le reste du planning n'a pas bougé).
        const rows = (result.rows || []).map((r) => firestoreDocToJob(r.id, r));
        const drop = new Set([...(result.removed_ids || []), ...(result.absorbed_id ? [result.absorbed_id] : []),
          ...task.tempIds, ...rows.map((r) => r.id)]);
        set((s) => ({
          jobs: [
            ...s.jobs.filter((j) => !drop.has(j.id)),
            ...rows.map((r) => {
              const prev = s.jobs.find((j) => j.id === r.id);
              return { ...r, carWindow: r.carWindow || prev?.carWindow || (r.ordre_id ? s.carWindows[r.ordre_id] : undefined) };
            }),
          ],
          charges: result.charge
            ? (result.charge.deleted
              ? s.charges.filter((c) => c.id !== result.charge!.id)
              : s.charges.map((c) => (c.id === result.charge!.id ? { ...c, hours: result.charge!.hours } : c)))
            : s.charges,
        }));
        if (result.local_only) {
          // MyO a refusé : Carvis garde le planning (jamais bloqué), MyO n'a pas suivi.
          const why = result.message || "MyO a refusé";
          setBackgroundTask(task.id, { status: "done", detail: "Enregistré dans Carvis seul" });
          showNotification({ type: "warning", message: `Enregistré dans Carvis, pas dans MyO : ${why}` });
        } else {
          setBackgroundTask(task.id, { status: "done", detail: "MyO mis à jour" });
        }
        // Ce qui gênait dans MyO : remis à sa place Carvis, re-posé ailleurs, ou à replacer.
        const fmtH = (h?: number) => (h === undefined ? "" : `${Math.floor(h)}h${String(Math.round((h % 1) * 60)).padStart(2, "0")}`);
        const fmtD = (d?: string) => (d ? d.slice(8, 10) + "/" + d.slice(5, 7) : "");
        for (const a of result.aligned || []) {
          showNotification({ type: "default", message: `${a.label} : MyO remis à sa place Carvis` });
        }
        for (const ev of result.evicted || []) {
          if (ev.replaced) {
            showNotification({ type: "default", message: `${ev.label} déplacé pour faire la place → ${fmtD(ev.date)} ${fmtH(ev.start_hour)}` });
          } else {
            showNotification({ type: "warning", message: `${ev.label} délogé pour faire la place : à replacer (dans « à répartir »)` });
          }
        }
        if ((result.aligned || []).length || (result.evicted || []).length) refetch = true;
      } catch (e: any) {
        task.rollback?.();
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 409 && detail?.code === "hors_horaires") {
          // Forçable (MyO l'accepte) : on demande plutôt que de refuser sec.
          set({ myoForcePending: { action: task.action, body: task.body, label: task.label, message: String(detail.message || "") } });
          setBackgroundTask(task.id, { status: "error", detail: "Hors des horaires : confirmation demandée" });
        } else {
          const message = typeof detail === "string" ? detail : "L'écriture dans MyO a échoué.";
          setBackgroundTask(task.id, { status: "error", detail: message });
          showNotification({ type: "error", message: /^MyO/.test(message) ? message : `MyO : ${message}` });
          // Une partie a pu passer avant le refus (le back resynchronise) : on relit.
          refetch = true;
        }
      } finally {
        const done = new Set(task.pendingIds);
        set((s) => ({
          myoBusy: null,
          myoPendingJobIds: s.myoPendingJobIds.filter((id) => !done.has(id)),
        }));
        if (refetch) {
          const orgId = get()._fetchedOrgId;
          if (orgId) {
            set({ isRefreshing: false });
            get().fetchData(orgId, true, true);
          }
        }
        task.resolve(ok);
      }
    }
  } finally {
    myoRunning = false;
  }
}

export const useAtelierStore = create<AtelierState>()(
  persist(
    (set, get) => ({
  selectedDate: todayIso(),
  viewMode: "jour",
  compaction: "normal" as CompactionMode,
  activeSpecialties: [],
  selectedWeekWorkerId: "",
  selectedWorkerIds: [],
  jobs: [],
  charges: [],
  workers: [],
  isLoading: false,
  isRefreshing: false,
  searchQuery: "",
  hoveredChargeId: null,
  highlightOrdreId: null,
  highlightJobId: null,
  carWindows: {},
  _fetchedOrgId: undefined,
  _loadedRange: undefined,
  _realtimeUnsubs: [],

  fetchData: async (orgId: string, force?: boolean, fresh?: boolean) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const range = getWeekRange(state.selectedDate);
    // Skip if we already have this week's data (unless forced)
    if (!force && state._loadedRange && state._loadedRange.date_from === range.date_from && state._loadedRange.date_to === range.date_to && state._fetchedOrgId === orgId) return;
    const showLoading = state.jobs.length === 0 && state.charges.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const [jobs, charges, blocks] = await Promise.all([
        atelierApi.listJobs({ org_id: orgId, date_from: range.date_from, date_to: range.date_to, limit: 1000, ...(fresh ? { fresh: true } : {}) }),
        atelierApi.listCharges({ org_id: orgId, limit: 1000, ...(fresh ? { fresh: true } : {}) }),
        disponibilitesApi.list({ org_id: orgId }),
      ]);

      // Derive workers from dispo blocks — anyone with a non-conge/pause block is a worker
      const orgMembers = useDataStore.getState().orgMembers;
      const memberMap = new Map(orgMembers.map((m) => [m.id, m]));
      const workerZones: Record<string, string[]> = {};
      for (const b of blocks) {
        const zone = b.zoneType || "mecanique";
        if (zone === "conge" || zone === "pause") continue;
        if (!workerZones[b.workerId]) workerZones[b.workerId] = [];
        if (!workerZones[b.workerId].includes(zone)) workerZones[b.workerId].push(zone);
      }

      const workers: WorkshopWorker[] = [];
      const workerIds = new Set<string>();
      // Off the planning this week: a stagiaire none of whose periods touch it, a leaver
      // past their exit date (Mélanie, 2026-10-08). A job still assigned to them keeps the column.
      const offThisWeek = (id: string) => {
        const cfg = (memberMap.get(id)?.config || {}) as any;
        if (cfg.exit_date && cfg.exit_date < range.date_from) return true;
        if (!cfg.stagiaire) return false;
        const periods: { from?: string; to?: string }[] = cfg.stagiaire_periods?.length
          ? cfg.stagiaire_periods
          : [{ from: cfg.stagiaire_from, to: cfg.stagiaire_to }];
        return !periods.some((p) => (!p?.from || p.from <= range.date_to) && (!p?.to || p.to >= range.date_from));
      };

      // Add all members with workshop roles (always visible, even without blocks)
      for (const m of orgMembers) {
        if (m.status === "inactive" || workerIds.has(m.id) || offThisWeek(m.id)) continue;
        const roleSpecialty = ROLE_TO_SPECIALTY[m.role];
        if (!roleSpecialty) continue;
        workers.push({
          id: m.id,
          name: m.infos?.display_name || m.infos?.full_name || "?",
          specialty: (m.config as any)?.team || roleSpecialty,
        });
        workerIds.add(m.id);
      }

      // Add anyone with dispo blocks who isn't already in the list
      Object.keys(workerZones).forEach((wid) => {
        if (workerIds.has(wid)) return;
        const member = memberMap.get(wid);
        if (!member || member.status === "inactive" || offThisWeek(wid)) return;
        const zones = workerZones[wid] || [];
        const specialty: WorkshopSpecialty =
          (member.config as any)?.team ||
          ROLE_TO_SPECIALTY[member.role] ||
          (zones.includes("mecanique") ? "mecanique" :
           zones.includes("carrosserie") ? "carrosserie" :
           zones.includes("service_rapide") ? "mecanique" : "mecanique");
        workers.push({
          id: member.id,
          name: member.infos?.display_name || member.infos?.full_name || "?",
          specialty,
        });
        workerIds.add(member.id);
      });

      // Ensure any worker referenced by a job is in the list
      for (const j of jobs) {
        if (j.workerId && !workerIds.has(j.workerId)) {
          const member = memberMap.get(j.workerId);
          if (member) {
            workers.push({
              id: member.id,
              name: member.infos?.display_name || member.infos?.full_name || "?",
              specialty: (member.config as any)?.team || ROLE_TO_SPECIALTY[member.role] || "autre",
            });
            workerIds.add(member.id);
          }
        }
      }

      // Rank set on the member card ("Ordre au planning atelier") = column position: 9 lands
      // 9th, not first (Mélanie, 2026-10-08). Unranked members fill the other places in order.
      const rankOf = (id: string) => {
        const r = (memberMap.get(id)?.config as any)?.atelier_order;
        return typeof r === "number" && r > 0 ? r : 0;
      };
      const ranked = workers.filter((w) => rankOf(w.id)).sort((a, b) => rankOf(a.id) - rankOf(b.id));
      const unranked = workers.filter((w) => !rankOf(w.id));
      workers.length = 0;
      while (ranked.length || unranked.length) {
        const pos = workers.length + 1;
        workers.push(ranked.length && (rankOf(ranked[0].id) <= pos || !unranked.length) ? ranked.shift()! : unranked.shift()!);
      }

      _cachedWorkers = workers;
      // Build carWindows from current jobs and charges only (bounded by fetched data)
      // Start fresh each fetch to avoid unbounded growth from stale entries
      const newCarWindows: Record<string, CarWindow> = {};
      for (const j of jobs) {
        if (j.ordre_id && j.carWindow) {
          newCarWindows[j.ordre_id] = j.carWindow;
        }
      }
      for (const c of charges) {
        if (c.ordre_id && c.carWindow) {
          newCarWindows[c.ordre_id] = c.carWindow;
        }
      }
      // Enrich jobs/charges that are missing carWindow from the persistent store
      const enrichedJobs = jobs.map((j) =>
        !j.carWindow && j.ordre_id && newCarWindows[j.ordre_id]
          ? { ...j, carWindow: newCarWindows[j.ordre_id] } : j,
      );
      const enrichedCharges = charges.map((c) =>
        !c.carWindow && c.ordre_id && newCarWindows[c.ordre_id]
          ? { ...c, carWindow: newCarWindows[c.ordre_id] } : c,
      );
      const currentWeekWorker = get().selectedWeekWorkerId;
      const validWeekWorker = currentWeekWorker === "tous" || workers.some((w) => w.id === currentWeekWorker);
      set({
        jobs: enrichedJobs, charges: enrichedCharges, workers, carWindows: newCarWindows, isLoading: false, isRefreshing: false, _loadedRange: range,
        ...((!currentWeekWorker || !validWeekWorker) && workers.length > 0 ? { selectedWeekWorkerId: workers[0].id } : {}),
      });
    } catch (error) {
      console.error("Error fetching atelier data:", error);
      set({ jobs: [], charges: [], workers: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined, _loadedRange: undefined });
    }
  },

  setSelectedDate: (date) => {
    const state = get();
    set({ selectedDate: date });
    // Keep the reception planning on the same day — the selected date is shared
    // across both plannings. Mirror the value (and force its next fetch) without
    // calling its setter, so there's no ping-pong. It re-fetches when opened.
    if (usePlanningStore.getState().selectedDate !== date) {
      usePlanningStore.setState({ selectedDate: date, _loadedRange: undefined });
    }
    // Re-fetch if the new date falls outside the loaded week
    const newRange = getWeekRange(date);
    if (state._fetchedOrgId && (!state._loadedRange || state._loadedRange.date_from !== newRange.date_from)) {
      set({ _loadedRange: undefined, isRefreshing: false });
      get().fetchData(state._fetchedOrgId);
    }
  },
  setViewMode: (mode) => set({ viewMode: mode }),
  setCompaction: (mode) => set({ compaction: mode }),
  toggleSpecialty: (s) => set((state) => {
    const cur = state.activeSpecialties ?? [];
    return { activeSpecialties: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
  }),
  setAllSpecialties: () => set({ activeSpecialties: [] }),
  setSelectedWeekWorkerId: (id) => set({ selectedWeekWorkerId: id }),
  toggleWorkerId: (id) => set((state) => {
    const ids = state.selectedWorkerIds;
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    // If empty after untoggling last one, reset to all workers
    return { selectedWorkerIds: next.length === 0 ? state.workers.map((w) => w.id) : next };
  }),
  setSelectedWorkerIds: (ids) => set({ selectedWorkerIds: ids }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setHoveredChargeId: (id) => set({ hoveredChargeId: id }),
  setHighlightOrdreId: (id) => {
    set({ highlightOrdreId: id });
    if (id) setTimeout(() => set((s) => s.highlightOrdreId === id ? { highlightOrdreId: null } : {}), 5000);
  },
  setHighlightJobId: (id) => {
    set({ highlightJobId: id });
    if (id) setTimeout(() => set((s) => s.highlightJobId === id ? { highlightJobId: null } : {}), 5000);
  },

  updateJob: (id, updates, opts) => {
    const current = get().jobs.find((j) => j.id === id);
    if (current && isMirroredJob(current)) {
      // Optimiste le temps de l'écriture ; la réponse remet l'état MyO relu, un refus
      // ne défait que ce créneau-là.
      set((state) => ({ jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...updates } : j)) }));
      const rollback = () => set((state) => ({ jobs: state.jobs.map((j) => (j.id === id ? current : j)) }));
      // Une autre durée = redimensionnement : MyO le fait avec le même appel que le
      // déplacement (sa poignée envoie la nouvelle durée du même magnet).
      const resized = updates.durationHours !== undefined && Math.abs(updates.durationHours - current.durationHours) > 1e-6;
      get().runMyo("move", {
        job_id: id,
        worker_id: updates.workerId ?? current.workerId,
        date: updates.date ?? current.date,
        start_hour: updates.startHour ?? current.startHour,
        ...(resized ? { duration_hours: updates.durationHours } : {}),
        ...(opts?.force ? { force: true } : {}),
      }, resized ? "Redimensionnement dans MyO…" : "Déplacement dans MyO…", { pendingIds: [id], rollback });
      return;
    }
    const positionChanged = updates.startHour !== undefined || updates.durationHours !== undefined || updates.workerId !== undefined;
    const wasAuto = positionChanged && get().jobs.find((j) => j.id === id)?.autoGenerated;
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...updates, ...(wasAuto ? { autoGenerated: false } : {}) } : j)),
    }));
    atelierApi.updateJob(id, updates as Partial<WorkshopJob>).catch((e) => console.error("Error updating job:", e));
    if (wasAuto) {
      atelierApi.confirmJob(id).catch((e) => console.error("Error confirming job:", e));
    }
  },

  placeChargeAsJob: (chargeId, workerId, startHour, durationHours, date, force) =>
    set((state) => {
      const charge = state.charges.find((c) => c.id === chargeId);
      if (!charge) return state;
      if (isMirroredCharge(charge)) {
        // Optimiste : le créneau apparaît tout de suite (« en cours ») ; la réponse le
        // remplace par la ligne MyO relue, un refus le retire et rend la charge.
        const tempId = `myo_${genId()}`;
        const left = +(charge.hours - durationHours).toFixed(2);
        const placeholder: WorkshopJob = {
          id: tempId,
          clientName: charge.clientName,
          plate: charge.plate || "",
          hours: durationHours,
          workerId,
          date: date ?? state.selectedDate,
          startHour,
          durationHours,
          status: "en_attente",
          specialty: charge.specialty,
          hasCarIcon: false,
          ordre_id: charge.ordre_id,
          org_id: charge.org_id,
          source: "servicebox",
          vehicleName: charge.vehicleName,
          carWindow: charge.carWindow || (charge.ordre_id ? state.carWindows[charge.ordre_id] : undefined),
        } as WorkshopJob;
        const rollback = () => set((s) => ({
          jobs: s.jobs.filter((j) => j.id !== tempId),
          charges: s.charges.some((c) => c.id === chargeId)
            ? s.charges.map((c) => (c.id === chargeId ? charge : c))
            : [...s.charges, charge],
        }));
        // Hors du set() en cours : runMyo pose myoBusy, que le « return state » écraserait.
        queueMicrotask(() => { get().runMyo("place", {
          charge_id: chargeId,
          worker_id: workerId,
          date: date ?? state.selectedDate,
          start_hour: startHour,
          duration_hours: durationHours,
          ...(force ? { force: true } : {}),
        }, "Planification dans MyO…", { pendingIds: [tempId], tempIds: [tempId], rollback }); });
        return {
          jobs: [...state.jobs, placeholder],
          charges: left > 0
            ? state.charges.map((c) => (c.id === chargeId ? { ...c, hours: left } : c))
            : state.charges.filter((c) => c.id !== chargeId),
        };
      }
      const worker = state.workers.find((w) => w.id === workerId);
      const orgId = charge.org_id || useDataStore.getState().orgData?.id || "";
      const jobDate = date ?? state.selectedDate;
      const tempId = genId();
      const newJob: WorkshopJob = {
        id: tempId,
        clientName: charge.clientName,
        plate: charge.plate || "",
        hours: durationHours,
        workerId,
        date: jobDate,
        startHour,
        durationHours,
        status: "en_cours",
        // Le métier du travail est celui de la charge : celui du compagnon ne sert que si
        // la charge n'en porte pas (sinon un changement de rôle reclassait les travaux).
        specialty: charge.specialty && charge.specialty !== "autre"
          ? charge.specialty
          : worker?.specialty || "autre",
        ordre_id: charge.ordre_id,
        org_id: orgId,
        carWindow: charge.carWindow || (charge.ordre_id ? state.carWindows[charge.ordre_id] : undefined),
      };
      const remainingHours = +(charge.hours - durationHours).toFixed(2);
      const newCharges = remainingHours > 0
        ? state.charges.map((c) => (c.id === chargeId ? { ...c, hours: remainingHours } : c))
        : state.charges.filter((c) => c.id !== chargeId);

      // Persist to API
      atelierApi.createJob({ ...newJob, org_id: orgId } as Omit<WorkshopJob, "id"> & { org_id: string }).then((created) => {
        // Replace temp id with real id, preserving carWindow since backend doesn't store it
        useAtelierStore.setState((s) => ({
          jobs: s.jobs.map((j) => (j.id === tempId ? { ...created, carWindow: j.carWindow ?? created.carWindow } : j)),
        }));
      }).catch((e) => console.error("Error creating job:", e));

      if (remainingHours > 0) {
        atelierApi.updateCharge(chargeId, { hours: remainingHours }).catch((e) => console.error("Error updating charge:", e));
      } else {
        atelierApi.deleteCharge(chargeId).catch((e) => console.error("Error deleting charge:", e));
      }

      // Persist carWindow before charge is potentially deleted
      const resolvedCarWindow = charge.carWindow || (charge.ordre_id ? state.carWindows[charge.ordre_id] : undefined);
      const updatedCarWindows = charge.ordre_id && resolvedCarWindow
        ? { ...state.carWindows, [charge.ordre_id]: resolvedCarWindow }
        : state.carWindows;
      return { jobs: [...state.jobs, newJob], charges: newCharges, carWindows: updatedCarWindows };
    }),

  splitCharge: (chargeId, keepHours) =>
    set((state) => {
      const charge = state.charges.find((c) => c.id === chargeId);
      if (!charge) return state;
      const remainder = +(charge.hours - keepHours).toFixed(2);
      if (remainder <= 0 || keepHours <= 0) return state;
      const orgId = charge.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: charge.clientName,
        hours: remainder,
        status: charge.status,
        specialty: charge.specialty,
        ordre_id: charge.ordre_id,
        org_id: orgId,
        carWindow: charge.carWindow,
      };

      // Persist: update original charge hours, create new charge for remainder
      atelierApi.updateCharge(chargeId, { hours: keepHours }).catch((e) => console.error("Error updating charge:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? { ...created, carWindow: c.carWindow ?? created.carWindow } : c)),
        }));
      }).catch((e) => console.error("Error creating split charge:", e));

      return {
        charges: [
          ...state.charges.map((c) => (c.id === chargeId ? { ...c, hours: keepHours } : c)),
          newCharge,
        ],
      };
    }),

  mergeCharges: (keepId, deleteId) =>
    set((state) => {
      const keep = state.charges.find((c) => c.id === keepId);
      const del = state.charges.find((c) => c.id === deleteId);
      if (!keep || !del) return state;
      // Une charge MyO est le reflet d'un RDV « à planifier » : fusionnée avec celle d'un
      // AUTRE RDV, la synchro recréait la supprimée et laissait l'autre gonflée.
      if ((isMirroredCharge(keep) || isMirroredCharge(del))
        && (keep.serviceboxRdvId || "").replace(/_part\d+$/, "") !== (del.serviceboxRdvId || "").replace(/_part\d+$/, "")) {
        showNotification({ type: "error", message: "Ces charges viennent de RDV MyO différents : elles ne se fusionnent pas (placez-les séparément)." });
        return state;
      }
      const mergedHours = +(keep.hours + del.hours).toFixed(2);
      atelierApi.updateCharge(keepId, { hours: mergedHours }).catch((e) => console.error("Error merging charge:", e));
      atelierApi.deleteCharge(deleteId).catch((e) => console.error("Error deleting merged charge:", e));
      return {
        charges: state.charges
          .filter((c) => c.id !== deleteId)
          .map((c) => (c.id === keepId ? { ...c, hours: mergedHours } : c)),
      };
    }),

  mergeJobs: (keepId, deleteId, newStartHour, newDurationHours) =>
    set((state) => {
      const keep = state.jobs.find((j) => j.id === keepId);
      const del = state.jobs.find((j) => j.id === deleteId);
      if (!keep || !del) return state;
      if (isMirroredJob(keep) || isMirroredJob(del)) {
        // Le magnet MyO porte la fusion : l'autre est un morceau du même RDV (fusion
        // native, après l'avoir collé) ou un bloc Carvis seul du même ordre (absorbé).
        const myo = isMirroredJob(keep) ? keep : del;
        const other = myo === keep ? del : keep;
        if (!isMirroredJob(other) && other.ordre_id !== myo.ordre_id) {
          showNotification({ type: "error", message: "Ces deux créneaux ne sont pas du même ordre." });
          return state;
        }
        const duration = isMirroredJob(other) ? newDurationHours : +(myo.durationHours + other.durationHours).toFixed(2);
        const rollback = () => set((s) => ({
          jobs: [...s.jobs.filter((j) => j.id !== keepId && j.id !== deleteId), keep, del],
        }));
        queueMicrotask(() => { get().runMyo("merge", {
          job_id: myo.id, other_job_id: other.id,
          start_hour: newStartHour, duration_hours: duration,
        }, "Fusion dans MyO…", { pendingIds: [myo.id], rollback }); });
        return {
          jobs: state.jobs
            .filter((j) => j.id !== other.id)
            .map((j) => (j.id === myo.id ? { ...j, startHour: newStartHour, durationHours: duration } : j)),
        };
      }
      atelierApi.updateJob(keepId, { startHour: newStartHour, durationHours: newDurationHours }).catch((e) => console.error("Error merging job:", e));
      atelierApi.deleteJob(deleteId).catch((e) => console.error("Error deleting merged job:", e));
      return {
        jobs: state.jobs
          .filter((j) => j.id !== deleteId)
          .map((j) => (j.id === keepId ? { ...j, startHour: newStartHour, durationHours: newDurationHours } : j)),
      };
    }),

  mergeChargeIntoJob: (chargeId, targetJobId, startHour, durationHours) => {
    const state = get();
    const charge = state.charges.find((c) => c.id === chargeId);
    const target = state.jobs.find((j) => j.id === targetJobId);
    // MyO porte ses propres magnets : pas de fusion locale qui le contournerait.
    if (!charge || !target || isMirroredJob(target) || isMirroredCharge(charge)) return;
    // Même tenue de compte que placeChargeAsJob : ce qui est posé quitte la charge.
    const added = +(durationHours - target.durationHours).toFixed(2);
    if (added <= 0) return;
    get().updateJob(targetJobId, { startHour, durationHours });
    const remainingHours = +(charge.hours - added).toFixed(2);
    set((s) => ({
      charges: remainingHours > 0
        ? s.charges.map((c) => (c.id === chargeId ? { ...c, hours: remainingHours } : c))
        : s.charges.filter((c) => c.id !== chargeId),
    }));
    if (remainingHours > 0) {
      atelierApi.updateCharge(chargeId, { hours: remainingHours }).catch((e) => console.error("Error updating charge:", e));
    } else {
      atelierApi.deleteCharge(chargeId).catch((e) => console.error("Error deleting charge:", e));
    }
  },

  returnJobToCharge: (jobId) =>
    set((state) => {
      const job = state.jobs.find((j) => j.id === jobId);
      if (!job) return state;
      if (isMirroredJob(job)) {
        // Hors du set() en cours : runMyo pose myoBusy, que le « return state » écraserait.
        queueMicrotask(() => { get().runMyo("unplan_one", { job_id: jobId }, "Report dans MyO…", { pendingIds: [jobId] }); });
        return state;
      }
      const orgId = job.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      // Carry carWindow from job itself, sibling charge, or persistent store
      const siblingCarWindow = job.carWindow
        || (job.ordre_id
          ? (state.charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
            || state.carWindows[job.ordre_id])
          : undefined);
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: job.clientName,
        hours: job.durationHours,
        status: "a_repartir",
        specialty: job.specialty,
        ordre_id: job.ordre_id,
        org_id: orgId,
        carWindow: siblingCarWindow,
      };
      atelierApi.deleteJob(jobId).catch((e) => console.error("Error deleting job:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? { ...created, carWindow: c.carWindow ?? created.carWindow } : c)),
        }));
      }).catch((e) => console.error("Error creating charge:", e));
      return {
        jobs: state.jobs.filter((j) => j.id !== jobId),
        charges: [...state.charges, newCharge],
      };
    }),

  returnInterventionToCharge: (jobId) =>
    set((state) => {
      const job = state.jobs.find((j) => j.id === jobId);
      if (!job) return state;
      if (isMirroredJob(job)) {
        // Hors du set() en cours : runMyo pose myoBusy, que le « return state » écraserait.
        queueMicrotask(() => { get().runMyo("unplan_all", { job_id: jobId }, "Replanification de l'intervention dans MyO…", { pendingIds: [jobId] }); });
        return state;
      }
      const pieces = [job, ...interventionSiblings(state.jobs, job)].filter((j) => !isMirroredJob(j));
      if (pieces.length === 0) return state;
      const ids = new Set(pieces.map((j) => j.id));
      const hours = +pieces.reduce((sum, j) => sum + j.durationHours, 0).toFixed(2);
      const orgId = job.org_id || useDataStore.getState().orgData?.id || "";
      pieces.forEach((j) => atelierApi.deleteJob(j.id).catch((e) => console.error("Error deleting job:", e)));

      // Une charge « à répartir » de la même intervention existe déjà : on la grossit
      // plutôt que d'en empiler une seconde.
      const existing = state.charges.find(
        (c) => c.ordre_id === job.ordre_id && c.specialty === job.specialty && c.status === "a_repartir",
      );
      if (existing) {
        const merged = +(existing.hours + hours).toFixed(2);
        atelierApi.updateCharge(existing.id, { hours: merged }).catch((e) => console.error("Error updating charge:", e));
        return {
          jobs: state.jobs.filter((j) => !ids.has(j.id)),
          charges: state.charges.map((c) => (c.id === existing.id ? { ...c, hours: merged } : c)),
        };
      }

      const tempId = genId();
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: job.clientName,
        hours,
        status: "a_repartir",
        specialty: job.specialty,
        ordre_id: job.ordre_id,
        org_id: orgId,
        carWindow: job.carWindow || (job.ordre_id ? state.carWindows[job.ordre_id] : undefined),
      };
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? { ...created, carWindow: c.carWindow ?? created.carWindow } : c)),
        }));
      }).catch((e) => console.error("Error creating charge:", e));
      return {
        jobs: state.jobs.filter((j) => !ids.has(j.id)),
        charges: [...state.charges, newCharge],
      };
    }),

  splitJob: (jobId, keepHours) =>
    set((state) => {
      const job = state.jobs.find((j) => j.id === jobId);
      if (!job) return state;
      if (isMirroredJob(job)) {
        // Hors du set() en cours : runMyo pose myoBusy, que le « return state » écraserait.
        queueMicrotask(() => { get().runMyo("split", { job_id: jobId, keep_hours: keepHours }, "Scission dans MyO…", { pendingIds: [jobId] }); });
        return state;
      }
      const remainder = +(job.durationHours - keepHours).toFixed(2);
      if (remainder <= 0 || keepHours <= 0) return state;
      const orgId = job.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: job.clientName,
        hours: remainder,
        status: "a_repartir",
        specialty: job.specialty,
        ordre_id: job.ordre_id,
        org_id: orgId,
        carWindow: job.carWindow || (job.ordre_id ? state.carWindows[job.ordre_id] : undefined),
      };
      atelierApi.updateJob(jobId, { durationHours: keepHours }).catch((e) => console.error("Error splitting job:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? { ...created, carWindow: c.carWindow ?? created.carWindow } : c)),
        }));
      }).catch((e) => console.error("Error creating split charge:", e));
      return {
        jobs: state.jobs.map((j) => (j.id === jobId ? { ...j, durationHours: keepHours } : j)),
        charges: [...state.charges, newCharge],
      };
    }),

  jobRemovalPending: null,
  jobSplitPending: null,
  myoBusy: null,
  myoForcePending: null,
  setMyoForcePending: (p) => set({ myoForcePending: p }),

  myoPendingJobIds: [],
  runMyo: (action, body, label, opts) => new Promise<boolean>((resolve) => {
    const id = `myo:${++myoSeq}`;
    const pendingIds = opts?.pendingIds ?? (body.job_id ? [body.job_id] : []);
    set((s) => ({ myoPendingJobIds: [...s.myoPendingJobIds, ...pendingIds] }));
    const waiting = myoRunning || myoQueue.length > 0;
    setBackgroundTask(id, {
      label: label.replace(/…$/, ""),
      status: waiting ? "waiting" : "running",
      detail: waiting ? "En attente de l'écriture MyO précédente" : "Écriture dans MyO…",
    });
    myoQueue.push({ id, action, body, label, pendingIds, tempIds: opts?.tempIds ?? [], rollback: opts?.rollback, resolve });
    void pumpMyo(set, get);
  }),
  setJobRemovalPending: (p) => set({ jobRemovalPending: p }),
  setJobSplitPending: (p) => set({ jobSplitPending: p }),

  requestJobRemoval: (jobId) => {
    const { jobs } = get();
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    // Plusieurs morceaux posés : « planifier un autre jour » (ce créneau) ou
    // « replanifier l'intervention » (tout), comme dans MyO. Un créneau MyO ne se
    // regroupe qu'avec les autres morceaux MyO (même RDV) ; un créneau Carvis, avec
    // les morceaux Carvis.
    if (interventionSiblings(jobs, job).some((j) => isMirroredJob(j) === isMirroredJob(job))) {
      set({ jobRemovalPending: { jobId } });
      return;
    }
    get().returnJobToCharge(jobId);
  },

  moveChargeToStatus: (chargeId, newStatus) => {
    set((state) => ({
      charges: state.charges.map((c) => (c.id === chargeId ? { ...c, status: newStatus } : c)),
    }));
    atelierApi.updateCharge(chargeId, { status: newStatus }).catch((e) => console.error("Error updating charge:", e));
  },

  deleteCharge: (chargeId) => {
    set((state) => ({ charges: state.charges.filter((c) => c.id !== chargeId) }));
    atelierApi.deleteCharge(chargeId).catch((e) => console.error("Error deleting charge:", e));
  },

  confirmJob: (jobId) => {
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === jobId ? { ...j, autoGenerated: false } : j)),
    }));
    atelierApi.confirmJob(jobId).catch((e) => console.error("Error confirming job:", e));
  },

  invalidateCarWindows: (ordreId) => {
    // Remove cached car window so it gets re-fetched from appointments on next fetchData
    set((state) => {
      const { [ordreId]: _, ...rest } = state.carWindows;
      return {
        carWindows: rest,
        // Also clear carWindow from jobs/charges for this ordre so they re-resolve
        jobs: state.jobs.map((j) => j.ordre_id === ordreId ? { ...j, carWindow: undefined } : j),
        charges: state.charges.map((c) => c.ordre_id === ordreId ? { ...c, carWindow: undefined } : c),
      };
    });
    // Re-fetch from API to get updated car windows (fresh : le cache serveur ne voit
    // pas les déplacements de RDV, seulement les jobs et les charges)
    const orgId = get()._fetchedOrgId;
    if (orgId) {
      set({ isRefreshing: false });
      get().fetchData(orgId, true, true);
    }
  },

  getFilteredJobs: () => {
    const state = get();
    const metiers = state.activeSpecialties ?? [];
    // Un job reste visible dans la colonne d'un compagnon affiché, quel que soit son
    // métier : un diag chez Jordan (mécanicien) disparaissait sous le filtre Mécanique
    // tout en bloquant le créneau (OR 1188120, 2026-10-20).
    const shownWorkers = new Set(state.workers.filter((w) => metiers.includes(w.specialty)).map((w) => w.id));
    return state.jobs.filter(
      (j) =>
        j.date === state.selectedDate &&
        (metiers.length === 0 || metiers.includes(j.specialty) || shownWorkers.has(j.workerId)) &&
        (state.searchQuery === "" ||
          j.clientName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
          j.plate.toLowerCase().includes(state.searchQuery.toLowerCase()))
    );
  },
  // Worker columns follow the active métier filter: selecting "Carrosserie" shows
  // only carrossiers, "Mécanique" only mécaniciens, etc. (empty filter = all).
  getVisibleWorkers: () => {
    const state = get();
    const metiers = state.activeSpecialties ?? [];
    if (metiers.length === 0) return state.workers;
    return state.workers.filter((w) => metiers.includes(w.specialty));
  },

  startRealtimeListener: (orgId: string) => {
    // Stop any existing listeners first
    get().stopRealtimeListener();
    const listenSince = Timestamp.now();
    const db = firebaseService.db;

    // Listen for job changes
    const jobsQuery = query(
      collection(db, "workshop_jobs"),
      where("org_id", "==", orgId),
      where("updated_at", ">=", listenSince),
    );
    const unsubJobs = onSnapshot(jobsQuery, (snapshot) => {
      if (snapshot.docChanges().length === 0) return;
      console.log(`[atelier-realtime] ${snapshot.docChanges().length} job(s) updated`);
      // Ce listener ne voit que les jobs modifiés depuis son ouverture : un job plus ancien
      // supprimé côté serveur (synchro MyO, envoi vers MyO) ne déclenche aucun « removed » et
      // reste affiché à côté de son remplaçant (doublons du 07/10). Un job inconnu qui arrive
      // = un traitement serveur est passé → relecture complète, groupée.
      const known = new Set(get().jobs.map((j) => j.id));
      if (snapshot.docChanges().some((c) => c.type === "added" && !known.has(c.doc.id))) {
        scheduleServerResync(orgId);
      }
      set((state) => {
        let jobs = [...state.jobs];
        const carWindows = { ...state.carWindows };
        for (const change of snapshot.docChanges()) {
          const doc = change.doc;
          const id = doc.id;
          if (change.type === "removed") {
            jobs = jobs.filter((j) => j.id !== id);
          } else {
            const newJob = firestoreDocToJob(id, doc.data());
            // Enrich with existing carWindow if available
            if (!newJob.carWindow && newJob.ordre_id && carWindows[newJob.ordre_id]) {
              newJob.carWindow = carWindows[newJob.ordre_id];
            }
            const idx = jobs.findIndex((j) => j.id === id);
            if (idx >= 0) {
              jobs[idx] = newJob;
            } else {
              // Race: realtime may fire before createJob response replaces the
              // temp gen_* id. Match by stable fields and swap in place.
              const tempIdx = jobs.findIndex((j) =>
                j.id.startsWith("gen_") &&
                j.ordre_id === newJob.ordre_id &&
                j.workerId === newJob.workerId &&
                j.date === newJob.date &&
                j.startHour === newJob.startHour,
              );
              if (tempIdx >= 0) {
                jobs[tempIdx] = newJob;
              } else {
                jobs.push(newJob);
              }
            }
          }
        }
        return { jobs };
      });
    }, (err) => {
      console.error("[atelier-realtime] jobs listener error:", err);
    });

    // Listen for charge changes
    const chargesQuery = query(
      collection(db, "work_charges"),
      where("org_id", "==", orgId),
      where("updated_at", ">=", listenSince),
    );
    const unsubCharges = onSnapshot(chargesQuery, (snapshot) => {
      if (snapshot.docChanges().length === 0) return;
      console.log(`[atelier-realtime] ${snapshot.docChanges().length} charge(s) updated`);
      set((state) => {
        let charges = [...state.charges];
        for (const change of snapshot.docChanges()) {
          const doc = change.doc;
          const id = doc.id;
          if (change.type === "removed") {
            charges = charges.filter((c) => c.id !== id);
          } else {
            const newCharge = firestoreDocToCharge(id, doc.data());
            // Enrich with existing carWindow if available
            if (!newCharge.carWindow && newCharge.ordre_id) {
              const cw = state.carWindows[newCharge.ordre_id];
              if (cw) newCharge.carWindow = cw;
            }
            const idx = charges.findIndex((c) => c.id === id);
            // Le document Firestore ne porte pas le statut de l'ordre (c'est un
            // enrichissement de l'API) : on garde celui déjà connu.
            if (idx >= 0 && !newCharge.ordreStatus) newCharge.ordreStatus = charges[idx].ordreStatus;
            // Idem pour le véhicule (marque modèle, plaque), résolu par l'API.
            if (idx >= 0 && !newCharge.vehicleName) newCharge.vehicleName = charges[idx].vehicleName;
            if (idx >= 0 && !newCharge.plate) newCharge.plate = charges[idx].plate;
            if (idx >= 0) {
              charges[idx] = newCharge;
            } else {
              // Race: realtime may fire before createCharge response replaces
              // the temp gen_* id. Match by stable fields and swap in place.
              const tempIdx = charges.findIndex((c) =>
                c.id.startsWith("gen_") &&
                c.ordre_id === newCharge.ordre_id &&
                c.specialty === newCharge.specialty &&
                c.status === newCharge.status &&
                Math.abs(c.hours - newCharge.hours) < 0.01,
              );
              if (tempIdx >= 0) {
                charges[tempIdx] = newCharge;
              } else {
                charges.push(newCharge);
              }
            }
          }
        }
        return { charges };
      });
    }, (err) => {
      console.error("[atelier-realtime] charges listener error:", err);
    });

    set({ _realtimeUnsubs: [unsubJobs, unsubCharges] });
  },

  stopRealtimeListener: () => {
    if (resyncTimer) clearTimeout(resyncTimer);
    resyncTimer = null;
    const unsubs = get()._realtimeUnsubs;
    for (const unsub of unsubs) unsub();
    set({ _realtimeUnsubs: [] });
  },
    }),
    {
      name: "atelier-prefs",
      version: 1,
      // v0 → v1: single `activeSpecialty` ("all" | métier) became `activeSpecialties` (string[], [] = Tous)
      migrate: (persisted: any, version: number) => {
        if (version < 1 && persisted && "activeSpecialty" in persisted) {
          const old = persisted.activeSpecialty;
          persisted.activeSpecialties = !old || old === "all" ? [] : [old];
          delete persisted.activeSpecialty;
        }
        return persisted;
      },
      partialize: (state) => ({
        viewMode: state.viewMode,
        compaction: state.compaction,
        selectedDate: state.selectedDate,
        activeSpecialties: state.activeSpecialties,
        selectedWeekWorkerId: state.selectedWeekWorkerId,
        selectedWorkerIds: state.selectedWorkerIds,
      }),
    },
  ),
);
